import { useCallback, useMemo, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { useCan } from '@/auth/consoleCapabilityContext';
import { useActingOrg } from '@/auth/actingOrgContext';
import {
    fetchMeAdmin,
    fetchOrgAdmins,
    fetchOrganization,
    fetchOrgLevels,
    fetchOrgTenants,
    fetchProvisioningStatus,
    OrgAdminApiError,
    OrgAdminUnauthorizedError,
    updateOrganization,
} from '@/api/orgAdmin';
import { ErrorState, Modal, Skeleton, useToast } from '@/components/ui';
import { OrgDetailHeader } from './organizations/OrgDetailHeader';
import { OrgOverviewTab } from './organizations/OrgOverviewTab';
import { OrgTenantsTab } from './organizations/OrgTenantsTab';
import { OrgAdminsTab } from './organizations/OrgAdminsTab';
import { OrgBrandingTab } from './organizations/OrgBrandingTab';

type DetailTab = 'overview' | 'tenants' | 'admins' | 'branding';

/**
 * EAA-1815: `/organizations/:id` — rebuilt per handoff frame "22 Org detail".
 *
 * Full page, not a drawer (the drawer on `/organizations` is a peek only —
 * see `OrganizationPeekDrawer`'s doc comment). Tab state lives in the URL
 * (`?tab=`), so a deep link into "Cơ sở" from the list/drawer lands on the
 * right tab and survives reload/back-forward.
 *
 * Gating (fail-closed, §1b): view → `console.orgs.view`; tenant/branding
 * writes → `org:{orgId}.manage` (super-admin has it for every org, an
 * org-admin only for their own — mirrors `HierarchyPage`'s exact gate, NOT
 * the blanket `console.orgs.manage`); admin issuance and the org-level
 * activate/deactivate toggle stay super-admin-only, matching the BE
 * (`OrganizationAdminsView.post`, `OrganizationDetailView.patch`) which
 * checks `is_super_admin` directly rather than a capability key.
 */
export function OrganizationDetailPage() {
    const { t } = useTranslation();
    const { orgId = '' } = useParams();
    const config = useRuntimeConfig();
    const { authenticated, accessToken, reauthenticate } = useAuth();
    const can = useCan();
    const acting = useActingOrg();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const toast = useToast();
    const [searchParams, setSearchParams] = useSearchParams();

    const tabs = useMemo(
        (): { key: DetailTab; label: string }[] => [
            { key: 'overview', label: t('admin.organizations.detail.tabs.overview') },
            { key: 'tenants', label: t('admin.organizations.tenantsLabel') },
            { key: 'admins', label: t('admin.organizations.detail.tabs.admins') },
            { key: 'branding', label: t('admin.organizations.detail.tabs.branding') },
        ],
        [t]
    );

    const rawTab = searchParams.get('tab');
    const tab: DetailTab =
        rawTab === 'tenants' || rawTab === 'admins' || rawTab === 'branding' ? rawTab : 'overview';
    const autoOpenCreate = tab === 'tenants' && searchParams.get('action') === 'create';

    const setTab = useCallback(
        (next: DetailTab) => {
            setSearchParams((prev) => {
                const params = new URLSearchParams(prev);
                params.delete('action');
                if (next === 'overview') params.delete('tab');
                else params.set('tab', next);
                return params;
            });
        },
        [setSearchParams]
    );
    const consumeAutoOpen = useCallback(() => {
        setSearchParams((prev) => {
            const params = new URLSearchParams(prev);
            params.delete('action');
            return params;
        });
    }, [setSearchParams]);

    const [deactivateConfirmOpen, setDeactivateConfirmOpen] = useState(false);
    const [toggleError, setToggleError] = useState<string | null>(null);

    const enabled =
        authenticated && Boolean(accessToken) && Boolean(orgId) && can('console.orgs.view');

    const meAdminQuery = useQuery({
        queryKey: ['me-admin', accessToken],
        queryFn: () => fetchMeAdmin(config.apiBase, accessToken as string),
        enabled,
        retry: false,
    });
    const orgQuery = useQuery({
        queryKey: ['organization', orgId, accessToken],
        queryFn: () => fetchOrganization(config.apiBase, accessToken as string, orgId),
        enabled,
        retry: false,
    });
    const tenantsQuery = useQuery({
        queryKey: ['organization-tenants', orgId, accessToken],
        queryFn: () => fetchOrgTenants(config.apiBase, accessToken as string, orgId),
        enabled,
        retry: false,
    });
    const adminsQuery = useQuery({
        queryKey: ['organization-admins', orgId, accessToken],
        queryFn: () => fetchOrgAdmins(config.apiBase, accessToken as string, orgId),
        enabled,
        retry: false,
    });
    const levelsQuery = useQuery({
        queryKey: ['org-levels', orgId, accessToken],
        queryFn: () => fetchOrgLevels(config.apiBase, accessToken as string, orgId),
        enabled,
        retry: false,
    });
    const coverageQuery = useQuery({
        queryKey: ['org-provisioning', orgId, accessToken],
        queryFn: () => fetchProvisioningStatus(config.apiBase, accessToken as string, orgId),
        enabled,
        retry: false,
    });

    // EAA-1825: loop-guarded — a 401 that portal-api keeps returning for a
    // freshly minted bearer used to reload this page forever via client.login().
    if (orgQuery.error instanceof OrgAdminUnauthorizedError) {
        reauthenticate(`/organizations/${orgId}`);
    }

    const isSuperAdmin = meAdminQuery.data?.is_super_admin ?? false;
    // EAA-1799-style gate: per-org manage, mirrors HierarchyPage exactly —
    // NOT the blanket console.orgs.manage.
    const canManage = Boolean(orgId) && can(`org:${orgId}.manage`);

    const org = orgQuery.data;
    const tenants = tenantsQuery.data ?? [];
    const admins = adminsQuery.data ?? [];

    const toggleOrgActiveMutation = useMutation({
        mutationFn: (is_active: boolean) =>
            updateOrganization(config.apiBase, accessToken as string, orgId, { is_active }),
        onSuccess: (_data, is_active) => {
            setToggleError(null);
            setDeactivateConfirmOpen(false);
            toast.success(
                is_active
                    ? t('admin.organizations.detail.toastReactivated')
                    : t('admin.organizations.detail.toastDeactivated')
            );
            void queryClient.invalidateQueries({ queryKey: ['organization', orgId] });
            void queryClient.invalidateQueries({ queryKey: ['organizations-list'] });
        },
        onError: (error) => {
            setToggleError(
                error instanceof OrgAdminApiError
                    ? error.message
                    : t('admin.organizations.detail.genericUpdateError')
            );
        },
    });

    // EAA-1778 (WP-7): org branding tints this page's accent — see the
    // pre-redesign doc comment this carries over verbatim.
    const brandColor = org?.branding?.primary_color;
    const brandStyle = brandColor
        ? ({ '--color-primary': brandColor, '--color-primary-hover': brandColor } as CSSProperties)
        : undefined;

    const goToTab = (next: DetailTab) => setTab(next);
    const viewHierarchy = () => {
        acting?.setActingOrg(orgId);
        navigate('/console/hierarchy');
    };

    if (!can('console.orgs.view')) {
        return null;
    }

    if (orgQuery.isError && !(orgQuery.error instanceof OrgAdminUnauthorizedError)) {
        return (
            <main className="portal-admin">
                <ErrorState
                    title={t('admin.organizations.detail.accessDeniedTitle')}
                    description={t('admin.organizations.detail.accessDeniedDescription')}
                />
                <Link to="/organizations">{t('admin.organizations.detail.backToList')}</Link>
            </main>
        );
    }

    return (
        <>
            <main className="portal-admin portal-console-page" style={brandStyle}>
                <span className="portal-org-detail-topline">
                    <span>{t('admin.organizations.orgWord')}</span>
                    <span>/</span>
                    <span style={{ color: 'var(--color-text)', fontWeight: 600 }}>
                        {org ? (
                            org.name
                        ) : (
                            <span role="status" aria-live="polite">
                                <span className="portal-sr-only">
                                    {t('admin.organizations.loadingSrOnly')}
                                </span>
                                <Skeleton width="8rem" height="1rem" />
                            </span>
                        )}
                    </span>
                </span>

                {!org && (
                    <div role="status" aria-live="polite" style={{ marginTop: 16 }}>
                        <span className="portal-sr-only">
                            {t('admin.organizations.detail.loadingOrgFull')}
                        </span>
                        <Skeleton height="3rem" />
                    </div>
                )}

                {org && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 16 }}>
                        <OrgDetailHeader
                            org={org}
                            tenantCount={tenants.length}
                            accountCount={coverageQuery.data?.assignments.active}
                            levelCount={levelsQuery.data?.count}
                            onViewHierarchy={viewHierarchy}
                            onAddTenant={() =>
                                setSearchParams((prev) => {
                                    const params = new URLSearchParams(prev);
                                    params.set('tab', 'tenants');
                                    params.set('action', 'create');
                                    return params;
                                })
                            }
                            canAddTenant={canManage}
                        />

                        {toggleError && <p className="portal-admin-error">{toggleError}</p>}

                        <nav
                            className="portal-org-detail-tabs"
                            aria-label={t('admin.organizations.detail.tabsAriaLabel')}
                        >
                            {tabs.map((tabItem) => (
                                <button
                                    key={tabItem.key}
                                    type="button"
                                    className={
                                        'portal-org-detail-tab' +
                                        (tabItem.key === tab ? ' is-active' : '')
                                    }
                                    onClick={() => goToTab(tabItem.key)}
                                >
                                    {tabItem.label}
                                    {tabItem.key === 'tenants' && (
                                        <span className="portal-org-detail-tab-count">
                                            {tenants.length}
                                        </span>
                                    )}
                                    {tabItem.key === 'admins' && (
                                        <span className="portal-org-detail-tab-count">
                                            {admins.length}
                                        </span>
                                    )}
                                </button>
                            ))}
                        </nav>

                        {tab === 'overview' && (
                            <OrgOverviewTab
                                org={org}
                                tenants={tenants}
                                tenantsLoading={tenantsQuery.isLoading}
                                admins={admins}
                                adminsLoading={adminsQuery.isLoading}
                                coverage={coverageQuery.data}
                                coverageLoading={coverageQuery.isLoading}
                                isSuperAdmin={isSuperAdmin}
                                canManage={canManage}
                                onAddTenant={() =>
                                    setSearchParams((prev) => {
                                        const params = new URLSearchParams(prev);
                                        params.set('tab', 'tenants');
                                        params.set('action', 'create');
                                        return params;
                                    })
                                }
                                onGoToTenants={() => goToTab('tenants')}
                                onGoToAdmins={() => goToTab('admins')}
                                onGoToBranding={() => goToTab('branding')}
                                onViewHierarchy={viewHierarchy}
                                onToggleOrgActive={() => {
                                    if (org.is_active) setDeactivateConfirmOpen(true);
                                    else toggleOrgActiveMutation.mutate(true);
                                }}
                                toggling={toggleOrgActiveMutation.isPending}
                            />
                        )}
                        {tab === 'tenants' && (
                            <OrgTenantsTab
                                org={org}
                                canManage={canManage}
                                autoOpenCreate={autoOpenCreate}
                                onAutoOpenConsumed={consumeAutoOpen}
                            />
                        )}
                        {tab === 'admins' && (
                            <OrgAdminsTab orgId={org.id} isSuperAdmin={isSuperAdmin} />
                        )}
                        {tab === 'branding' && <OrgBrandingTab org={org} canManage={canManage} />}
                    </div>
                )}
            </main>

            <Modal
                open={deactivateConfirmOpen}
                onCancel={() => {
                    if (toggleOrgActiveMutation.isPending) return;
                    setDeactivateConfirmOpen(false);
                }}
                title={t('admin.organizations.detail.deactivateModal.title')}
                width={420}
                footer={
                    <>
                        <button
                            type="button"
                            className="portal-btn"
                            onClick={() => setDeactivateConfirmOpen(false)}
                            disabled={toggleOrgActiveMutation.isPending}
                        >
                            {t('common.cancel')}
                        </button>
                        <button
                            type="button"
                            className="portal-btn portal-btn-danger"
                            disabled={toggleOrgActiveMutation.isPending}
                            onClick={() => toggleOrgActiveMutation.mutate(false)}
                        >
                            {toggleOrgActiveMutation.isPending
                                ? t('admin.organizations.processingLabel')
                                : t('admin.organizations.deactivateAction')}
                        </button>
                    </>
                }
            >
                <p>
                    {t('admin.organizations.detail.deactivateModal.bodyPrefix')}{' '}
                    <strong>{org?.name}</strong>{' '}
                    {t('admin.organizations.detail.deactivateModal.bodySuffix')}
                </p>
            </Modal>
        </>
    );
}
